import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  TextField,
  Typography,
  Checkbox,
  useMediaQuery,
} from "@mui/material";
import { Emoji } from "emoji-picker-react";
import AttributeIndicator from "../../../../tasks/taskCard/AttributeIndicator";
import sortTasks from "../../../../../helpers/sort";
import { ReactComponent as TargetIcon } from "../../../../../assets/icons/crosshair.svg";

/**
 * Individual task item in the task picker list
 */
const TaskPickerItem = ({ task, isSelected, onToggle, useShortLabels }) => {
  const { importance, fun, difficulty } = task.attributes || {};

  return (
    <Box
      className={`edit-task-block__task-item ${isSelected ? "edit-task-block__task-item--selected" : ""}`}
      onClick={onToggle}
    >
      <Box className="edit-task-block__task-header">
        <Checkbox
          checked={isSelected}
          size="small"
          sx={{ padding: 0, marginRight: 1 }}
        />
        {task.emoji && (
          <span className="edit-task-block__task-emoji">
            <Emoji unified={task.emoji} size={16} />
          </span>
        )}
        {task.targeted && (
          <TargetIcon
            style={{
              width: 16,
              height: 16,
              fill: "transparent",
              stroke: "#ffa600", // $dark-yellow
              marginRight: 4,
              flexShrink: 0,
            }}
          />
        )}
        <Typography variant="body2" className="edit-task-block__task-title">
          {task.title}
        </Typography>
      </Box>
      <Box className="edit-task-block__task-attributes">
        {importance && (
          <Box className="edit-task-block__attribute">
            <Typography
              variant="caption"
              className="edit-task-block__attribute-label"
            >
              {useShortLabels ? "Imp" : "Importance"}
            </Typography>
            <AttributeIndicator value={importance} />
          </Box>
        )}
        {difficulty && (
          <Box className="edit-task-block__attribute">
            <Typography
              variant="caption"
              className="edit-task-block__attribute-label"
            >
              {useShortLabels ? "Dif" : "Difficulty"}
            </Typography>
            <AttributeIndicator value={difficulty} />
          </Box>
        )}
        {fun && (
          <Box className="edit-task-block__attribute">
            <Typography
              variant="caption"
              className="edit-task-block__attribute-label"
            >
              Fun
            </Typography>
            <AttributeIndicator value={fun} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * Reusable task picker with search functionality for block editing/creation
 *
 * @param {Object} props
 * @param {Array} props.tasks - Array of all available task objects
 * @param {Array} props.selectedTaskIds - Array of currently selected task IDs
 * @param {function} props.onTaskToggle - Callback when a task is toggled (receives taskId)
 * @param {string} [props.searchQuery=""] - Current search query
 * @param {function} [props.onSearchChange] - Callback when search query changes
 * @param {boolean} [props.showSearch=true] - Whether to show the search field
 * @param {string} [props.headerText] - Optional header text (defaults to "Tasks (X selected)")
 * @param {Object} [props.blocksData] - Object of all blocks to check for task conflicts
 * @param {number|string} [props.currentBlockId] - The ID of the current block being edited (to exclude from duplicate check)
 */
const TaskPicker = ({
  tasks,
  selectedTaskIds,
  onTaskToggle,
  searchQuery = "",
  onSearchChange,
  showSearch = true,
  headerText,
  blocksData,
  currentBlockId,
}) => {
  // Get sort settings from Redux to match task list sorting
  const { sortOrder, mindset } = useSelector((state) => state.user);

  // Use short labels on very small screens (iPhone 5/SE = 320px)
  const useShortLabels = useMediaQuery("(max-width: 360px)");

  // Collect all task IDs already used in other blocks
  const usedTaskIds = useMemo(() => {
    if (!blocksData) return new Set();

    const used = new Set();
    Object.entries(blocksData).forEach(([blockId, block]) => {
      // Skip the current block being edited
      if (currentBlockId && blockId == currentBlockId) return;

      // Collect task IDs from this block
      if (
        block.type === "task" &&
        block.taskIds &&
        Array.isArray(block.taskIds)
      ) {
        block.taskIds.forEach((taskId) => used.add(taskId));
      }
    });

    return used;
  }, [blocksData, currentBlockId]);

  // Sort and filter tasks using the same logic as the task list
  const sortedAndFilteredTasks = useMemo(() => {
    // First filter by search query and exclude tasks already in other blocks
    const searchFiltered = tasks.filter((task) => {
      // Exclude tasks already used in other blocks
      if (usedTaskIds.has(task.id)) return false;

      if (!searchQuery.trim()) return true;
      return task.title.toLowerCase().includes(searchQuery.toLowerCase());
    });

    // Convert to format expected by sortTasks (uses 'name' instead of 'title')
    const tasksToSort = searchFiltered.map((task) => ({
      ...task,
      name: task.title,
    }));

    // Sort using the same function as the task list
    return sortTasks({
      tasksToSort,
      sortOrder,
      mindset,
    });
  }, [tasks, searchQuery, sortOrder, mindset, usedTaskIds]);

  const header =
    headerText !== undefined
      ? headerText
      : `Tasks (${selectedTaskIds.length} selected)`;

  return (
    <Box>
      {header && (
        <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
          {header}
        </Typography>
      )}
      {showSearch && onSearchChange && (
        <TextField
          label="Search tasks"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by task name..."
          size="small"
          fullWidth
          sx={{ mb: 1 }}
        />
      )}
      <Box className="edit-task-block__task-list scroller">
        {sortedAndFilteredTasks.map((task) => (
          <TaskPickerItem
            key={task.id}
            task={task}
            isSelected={selectedTaskIds.includes(task.id)}
            onToggle={() => onTaskToggle(task.id)}
            useShortLabels={useShortLabels}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TaskPicker;
